import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { http, formatError } from "@/lib/api";

export const StaffCtx = createContext(null);
export const useStaff = () => useContext(StaffCtx);

export const ROLE_NAMES = { owner: "Owner", super_admin: "Super Admin", admin: "Admin", billing: "Billing (old role)", support: "Support (old role)" };
export const PLAN_NAMES = { free: "Free", starter: "Starter", plus: "Plus", unlimited: "Unlimited", admin: "Owner (unlimited)" };
export const FEATURE_NAMES = { practice: "Practice & flashcards", progress: "Progress", groups: "Study Groups", web: "Web research", draw: "Draw it" };
export const ACTIONS = {
  "student.view": "Opened student record", "plan.grant": "Gave a free plan", "plan.remove": "Removed a free plan",
  "pass.grant": "Gave a free pass", "pass.remove": "Removed a pass", "limits.set": "Set personal limits",
  "features.set": "Changed features", "sessions.revoke": "Signed out all devices", "account.suspend": "Suspended account",
  "account.unsuspend": "Restored account", "deletion.schedule": "Scheduled deletion", "deletion.cancel": "Cancelled deletion",
  "staff.grant": "Added to staff", "staff.update": "Changed staff access", "staff.remove": "Removed from staff",
  "staff.migrated": "Moved to Admin (same permissions)", "staff.identity_confirmed": "Confirmed identity",
  "ai_rates.update": "Changed AI cost rates", "plan_limits.update": "Changed plan limits", "discount.create": "Created discount code",
  "discount.toggle": "Switched discount code", "discount.delete": "Deleted discount code", "discount.edit": "Edited discount code",
  "discount.auto_off": "Switched off a full discount code",
};

export const usd = (n, d = 2) => `$${Number(n || 0).toFixed(d)}`;
export const num = (n) => Number(n || 0).toLocaleString();
export const when = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
export const day = (s) => (s ? new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—");
export const money = (byCur) => {
  const rows = Object.entries(byCur || {});
  return rows.length ? rows.map(([c, v]) => `${c === "USD" ? "$" : `${c} `}${Number(v).toFixed(2)}`).join(" · ") : "$0.00";
};

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const defaultRange = (preset = "7d") => {
  const t = new Date();
  return { preset, from: ymd(new Date(t.getTime() - 30 * 864e5)), to: ymd(t) };
};

// Call inside useMemo keyed on the range: "now" changes on every call.
export function rangeParams(r) {
  const now = new Date();
  if (r.preset === "custom") {
    const f = new Date(`${r.from}T00:00:00`), t = new Date(`${r.to}T23:59:59.999`);
    return Number.isNaN(f.getTime()) || Number.isNaN(t.getTime()) ? {} : { frm: f.toISOString(), to: t.toISOString() };
  }
  if (r.preset === "today") return { frm: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(), to: now.toISOString() };
  return { frm: new Date(now.getTime() - (r.preset === "30d" ? 30 : 7) * 864e5).toISOString(), to: now.toISOString() };
}

export function useLoad(path, params) {
  const key = JSON.stringify(params || {});
  const seq = useRef(0);
  const [s, setS] = useState({ data: null, error: "", loading: true });
  const reload = useCallback(() => {
    const n = ++seq.current;
    setS((p) => ({ ...p, loading: true, error: "" }));
    return http.get(path, { params: JSON.parse(key) })
      .then((r) => n === seq.current && setS({ data: r.data, error: "", loading: false }))
      .catch((e) => n === seq.current && setS((p) => ({ data: p.data, error: formatError(e), loading: false })));
  }, [path, key]);
  useEffect(() => { reload(); }, [reload]);
  return { ...s, reload };
}

export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// A confirmed identity lasts 10 minutes on this device (the server checks it too).
const VERIFIED_KEY = "brynai.staffVerified";
export const STEP_KEY = "brynai.staffStep";
export function getVerified() {
  try {
    const v = JSON.parse(sessionStorage.getItem(VERIFIED_KEY) || "null");
    return v && v.until > Date.now() ? v.token : null;
  } catch {
    return null;
  }
}
export const verifiedUntil = () => {
  try { return JSON.parse(sessionStorage.getItem(VERIFIED_KEY) || "null")?.until || 0; } catch { return 0; }
};
export const setVerified = (token, secs) => sessionStorage.setItem(VERIFIED_KEY, JSON.stringify({ token, until: Date.now() + (secs - 30) * 1000 }));
export const clearVerified = () => sessionStorage.removeItem(VERIFIED_KEY);
export const isVerifyError = (e) => e?.response?.data?.detail?.code === "staff_verify";
